// =============================================================================
// Slot Step — selezione giorno + slot + eventuale preferenza risorsa
// =============================================================================

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, format, startOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarDays, Globe, Loader2 } from 'lucide-react';
import { usePublicAvailability, type PublicService, type PublicTenantInfo } from '../../api/public-booking-hooks';
import { useDateLocale } from '../../hooks/useDateLocale';
import { PrimaryButton } from './PrimaryButton';
import { PublicDayPicker } from './PublicDayPicker';
import {
  browserTimeZone,
  civilDateInTz,
  civilDayOf,
  civilDayToCarrier,
  endOfCivilDayInTz,
  startOfCivilDayInTz,
  timeStringInTz,
  timeZoneAbbreviation,
  timeZoneLabel,
  timeZonesDiffer,
} from '../../lib/tenantTime';
import { ResourceCombobox } from '../ui/ResourceCombobox';

interface Props {
  tenantSlug: string;
  service: PublicService;
  tenant: PublicTenantInfo;
  initialStart: string | null;
  initialAllocations: Array<{ resourceTypeId: string; resourceId?: string }>;
  onBack: () => void;
  onConfirm: (startIso: string, allocations: Array<{ resourceTypeId: string; resourceId?: string }>) => void;
}

export function SlotStep({
  tenantSlug,
  service,
  tenant,
  initialStart,
  initialAllocations,
  onBack,
  onConfirm,
}: Props) {
  const { t } = useTranslation('public-booking');
  const dateLocale = useDateLocale();

  // Il fuso dell'attivita': tutti gli orari di questa pagina sono espressi li'.
  // L'avviso compare solo quando il visitatore e' altrove: per la maggioranza
  // dei clienti, che prenota dalla stessa citta', sarebbe solo rumore.
  const timeZone = tenant.timezone;

  // La finestra minima/massima è ereditata dal tenant. Il preavviso minimo e'
  // una durata reale, ma il GIORNO da cui si puo' prenotare e' quello civile
  // dell'attivita'.
  const minDate = useMemo(() => {
    const earliest = new Date(Date.now() + tenant.minAdvanceHours * 60 * 60 * 1000);
    return civilDayToCarrier(civilDateInTz(earliest, timeZone));
  }, [tenant.minAdvanceHours, timeZone]);
  const maxDate = useMemo(() => addDays(minDate, tenant.maxAdvanceDays), [minDate, tenant.maxAdvanceDays]);

  const [day, setDay] = useState<Date>(() => {
    if (initialStart) return civilDayToCarrier(civilDateInTz(new Date(initialStart), timeZone));
    return minDate;
  });
  const showTimeZoneNote = useMemo(
    () => timeZonesDiffer(timeZone, browserTimeZone()),
    [timeZone],
  );

  const [selected, setSelected] = useState<string | null>(initialStart);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [prefs, setPrefs] = useState<Map<string, string>>(() => {
    const m = new Map<string, string>();
    for (const a of initialAllocations) if (a.resourceId) m.set(a.resourceTypeId, a.resourceId);
    return m;
  });

  const range = useMemo(
    () => ({
      from: startOfCivilDayInTz(civilDayOf(day), timeZone).toISOString(),
      to: endOfCivilDayInTz(civilDayOf(day), timeZone).toISOString(),
    }),
    [day, timeZone],
  );

  const slotsQuery = usePublicAvailability(tenantSlug, {
    serviceId: service.id,
    from: range.from,
    to: range.to,
  });

  const slots = slotsQuery.data ?? [];

  const shiftDay = (delta: number) => {
    const next = addDays(day, delta);
    if (next < minDate) return;
    if (next > maxDate) return;
    setDay(next);
    setSelected(null);
  };

  const pickDay = (next: Date) => {
    setDay(startOfDay(next));
    setSelected(null);
  };

  const handleConfirm = () => {
    if (!selected) return;
    const allocations: Array<{ resourceTypeId: string; resourceId?: string }> = [];
    for (const [resourceTypeId, resourceId] of prefs.entries()) {
      if (resourceId) allocations.push({ resourceTypeId, resourceId });
    }
    onConfirm(selected, allocations);
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900">{t('slot.title')}</h2>

      {/* Day navigator */}
      <div className="flex items-center justify-between bg-gray-50 rounded-xl p-2 ring-1 ring-gray-100">
        <button
          onClick={() => shiftDay(-1)}
          disabled={day <= minDate}
          className="p-2 rounded-lg hover:bg-white hover:shadow-sm disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:shadow-none transition-all"
          aria-label="Prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 px-3 py-1.5 rounded-lg hover:bg-white hover:shadow-sm capitalize transition-all"
            aria-label={t('slot.chooseDate')}
          >
            <CalendarDays className="w-4 h-4 text-gray-400" />
            {format(day, 'EEEE d MMMM yyyy', { locale: dateLocale })}
          </button>
          {pickerOpen && (
            <PublicDayPicker
              value={day}
              min={minDate}
              max={maxDate}
              onSelect={pickDay}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
        <button
          onClick={() => shiftDay(1)}
          disabled={day >= maxDate}
          className="p-2 rounded-lg hover:bg-white hover:shadow-sm disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:shadow-none transition-all"
          aria-label="Next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Preferenze risorse (solo se il servizio lo consente) */}
      {service.allowResourceSelection && service.selectableResources && service.selectableResources.length > 0 && (
        <div className="space-y-3">
          {service.selectableResources.map((group) =>
            group.resources.length === 0 ? null : (
              <div key={group.resourceTypeId}>
                <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                  {t('slot.preferredResource', { type: group.resourceTypeName })}
                </label>
                <ResourceCombobox
                  resources={group.resources}
                  value={prefs.get(group.resourceTypeId) ?? ''}
                  onChange={(id) => {
                    const m = new Map(prefs);
                    if (id === '') m.delete(group.resourceTypeId);
                    else m.set(group.resourceTypeId, id);
                    setPrefs(m);
                    setSelected(null);
                  }}
                  placeholder={t('slot.searchResource')}
                  emptyLabel={t('slot.anyResource')}
                  noMatchLabel={t('slot.noResourceMatch')}
                />
              </div>
            ),
          )}
          <p className="text-xs text-gray-500">{t('slot.preferredHint')}</p>
        </div>
      )}

      {/* Slot grid */}
      {slotsQuery.isLoading ? (
        <div className="flex items-center justify-center py-10 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('loading')}
        </div>
      ) : slots.length === 0 ? (
        <div className="text-center py-10 text-gray-500 text-sm">{t('slot.noSlots')}</div>
      ) : (
        <>
        {showTimeZoneNote && (
          // Il cliente e' in un fuso diverso: senza questa riga leggerebbe gli
          // orari come se fossero i suoi e si presenterebbe all'ora sbagliata.
          <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 mb-3">
            <Globe className="w-3.5 h-3.5 flex-shrink-0" />
            {t('slot.timeZoneNote', {
              zone: timeZoneLabel(timeZone),
              abbr: timeZoneAbbreviation(timeZone),
            })}
          </p>
        )}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {slots.map((s) => {
            const active = selected === s.start;
            return (
              <button
                key={s.start}
                onClick={() => setSelected(s.start)}
                className={`px-2 py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                  active
                    ? 'text-white border-transparent shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-[color:var(--skedulab-public-primary)] hover:-translate-y-px'
                }`}
                style={active ? { background: 'var(--skedulab-public-primary, #10B981)' } : undefined}
              >
                {timeStringInTz(new Date(s.start), timeZone)}
              </button>
            );
          })}
        </div>
        </>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <button onClick={onBack} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
          ← {t('actions.back')}
        </button>
        <PrimaryButton disabled={!selected} onClick={handleConfirm}>
          {t('actions.next')}
        </PrimaryButton>
      </div>
    </div>
  );
}
